import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { chatPostOnDemandAtom, contentDetailOnDemandAtom } from '@/state/atom/translationAtom';
import { corporationAtom } from '@/state/atom/corporationAtom';
import CorporationUtils from '@/common/utils/CorporationUtils';
import Constant from '@/common/constant/Constant';
import TranslationConstant from '@/common/constant/TranslationConstant';
import Utils from '@/common/utils/utils';

const { ON_DEMAND_TRANSLATION_VIEW } = TranslationConstant;

/**
 * Resolve the outline/title a header should DISPLAY for on-demand translation.
 *
 * When the 「翻訳版」toggle is ON (activeView = TRANSLATION) and a translated
 * outline is available, the hook reports the translation and flags
 * `isShowingTranslation`; otherwise it reports the original outline passed in by
 * the caller (each header already computes its own original title).
 *
 * Serves both on-demand features, which keep their state in separate atoms:
 *   - content detail (ticket / knowledge / email child): keyed by contentId,
 *     translated title in `translatedOutline`.
 *   - chat thread: keyed by chatThreadId, translated title in
 *     `translatedTicketOutline` (a ticket has ONE outline copied onto each of its
 *     thread records).
 * Ids from the two atoms never collide, so the record is simply looked up in both.
 *
 * @param {string} originalOutline - The title the header normally renders.
 * @param {string} recordId - contentId for a detail header, chatThreadId for a chat header.
 * @returns {{ displayOutline: string, isShowingTranslation: boolean }}
 */
const useOnDemandOutlineDisplay = (
    originalOutline,
    recordId,
) => {
    const contentDetailOnDemand = useRecoilValue(contentDetailOnDemandAtom);
    const chatPostOnDemand = useRecoilValue(chatPostOnDemandAtom);
    const corporationDetail = useRecoilValue(corporationAtom);

    const record = recordId
        ? (contentDetailOnDemand[recordId] || chatPostOnDemand[recordId])
        : null;

    const isChatThreadRecord = !Utils.isEmptyString(record?.threadType);
    const isTicketEditMode = !isChatThreadRecord
        ? record?.ticketMode === Constant.DISPLAY_MODE.EDIT
        : false;

    // Fallback (chat thread only): a ticket has ONE outline, but the translated title is
    // only copied onto the thread records that existed when the translation response
    // arrived. A thread opened afterwards has no own copy, and getTicketOutlineNeedTranslate
    // will not resend the title (it is already translated for the ticket), so reuse the
    // translation of a sibling thread of the same ticket with the same original outline.
    const chatRecord = recordId && !contentDetailOnDemand[recordId] ? chatPostOnDemand[recordId] : null;
    const siblingTranslatedOutline = useMemo(() => {
        if (!chatRecord || !Utils.isEmptyString(chatRecord.translatedTicketOutline)) return null;
        if (Utils.isEmptyString(chatRecord.ticketId) || Utils.isEmptyString(chatRecord.ticketOutline)) return null;

        const siblingRecord = Object.values(chatPostOnDemand).find(
            (threadRecord) =>
                threadRecord?.ticketId === chatRecord.ticketId &&
                threadRecord?.ticketOutline === chatRecord.ticketOutline &&
                !Utils.isEmptyString(threadRecord?.translatedTicketOutline)
        );
        return siblingRecord?.translatedTicketOutline ?? null;
    }, [chatPostOnDemand, chatRecord]);

    // Org multi-language off -> always the original title, never the cached translation.
    const isMultiLanguageOn = CorporationUtils.isEnableSettingMultiLanguage(corporationDetail);
    const isTranslationView = record?.activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION;
    const translatedOutline = record?.translatedOutline ?? record?.translatedTicketOutline ?? siblingTranslatedOutline;

    const isShowingTranslation = Boolean(
        isMultiLanguageOn && !isTicketEditMode && isTranslationView && !Utils.isEmptyString(translatedOutline)
    );

    return {
        displayOutline: isShowingTranslation ? translatedOutline : originalOutline,
        isShowingTranslation
    };
};

export default useOnDemandOutlineDisplay;
